import React, {useEffect, useRef, useState} from "react"
import {Accordion, AccordionDetails, AccordionSummary, Box, Button, IconButton, Typography} from "@mui/material"
import CloseIcon from "@mui/icons-material/Close"
import ExpandMoreIcon from "@mui/icons-material/ExpandMore"

import {RuleText} from "./RuleText"

import type {CaseManual} from "@indebted/api"
import {colors} from "@indebted/theme/colors"

const defaultWidth = 480
const minWidth = 320
const maxViewportShare = 0.6
const keyboardStep = 16
const widthKey = "caseManualsPanel.width"

type CaseManualsPanelProps = {
	caseType: string
	manuals: CaseManual[]
	onClose: () => void
}

// Docked beside the page rather than over it: sticky for the viewport's
// height, its own scroll, and a drag handle on the edge facing the page.
function CaseManualsPanel({caseType, manuals, onClose}: CaseManualsPanelProps): JSX.Element {
	const [width, setWidth] = useState(() => clampWidth(rememberedWidth()))
	const [open, setOpen] = useState(() => firstExpanded(manuals))
	// The panel stays mounted when another case's manuals arrive; open state
	// from the previous set must not decide what the new one shows.
	const [shownFor, setShownFor] = useState(manuals)
	if (shownFor !== manuals) {
		setShownFor(manuals)
		setOpen(firstExpanded(manuals))
	}
	const allOpen = manuals.every((manual) => open.has(manual.Name))
	const toggle = (name: string) =>
		setOpen((current) => {
			const next = new Set(current)
			if (next.has(name)) next.delete(name)
			else next.add(name)
			return next
		})
	const endDrag = useRef<(() => void) | null>(null)

	// A drag whose pointerup never arrives — button released outside the
	// window, pointer cancelled, panel unmounted — must still end.
	useEffect(() => () => endDrag.current?.(), [])

	const startResize = (event: React.PointerEvent) => {
		event.preventDefault()
		endDrag.current?.()
		const startX = event.clientX
		const startWidth = width
		let latest = width
		const follow = (move: PointerEvent) => {
			latest = clampWidth(startWidth + startX - move.clientX)
			setWidth(latest)
		}
		const release = () => {
			window.removeEventListener("pointermove", follow)
			window.removeEventListener("pointerup", release)
			window.removeEventListener("pointercancel", release)
			window.removeEventListener("blur", release)
			endDrag.current = null
			rememberWidth(latest)
		}
		window.addEventListener("pointermove", follow)
		window.addEventListener("pointerup", release)
		window.addEventListener("pointercancel", release)
		window.addEventListener("blur", release)
		endDrag.current = release
	}

	const nudge = (event: React.KeyboardEvent) => {
		const step = event.key === "ArrowLeft" ? keyboardStep : event.key === "ArrowRight" ? -keyboardStep : 0
		if (step === 0) return
		event.preventDefault()
		const next = clampWidth(width + step)
		setWidth(next)
		rememberWidth(next)
	}

	return (
		<Box
			component="aside"
			aria-label="Client & case manuals"
			sx={{
				position: "sticky",
				top: 0,
				alignSelf: "flex-start",
				flex: "none",
				height: "100vh",
				display: "flex",
				width: {xs: "100%", md: width},
				minWidth: {md: minWidth},
				maxWidth: {md: `${maxViewportShare * 100}vw`},
				borderLeft: `1px solid ${colors.gray["200"]}`,
				bgcolor: "background.paper",
			}}
		>
			<Box
				role="separator"
				aria-orientation="vertical"
				aria-label="Resize manuals panel"
				aria-valuenow={width}
				aria-valuemin={minWidth}
				aria-valuemax={widestAllowed()}
				tabIndex={0}
				onPointerDown={startResize}
				onKeyDown={nudge}
				sx={{
					position: "absolute",
					top: 0,
					bottom: 0,
					left: -4,
					width: 8,
					cursor: "col-resize",
					touchAction: "none",
					"&:hover, &:focus-visible, &:active": {bgcolor: colors.warmBlue["200"], outline: "none"},
				}}
			/>
			<Box flex={1} minWidth={0} overflow="auto" p={4} display="flex" flexDirection="column" gap={4}>
				<Box display="flex" justifyContent="space-between" alignItems="flex-start" gap={2}>
					<Box>
						<Typography variant="h6">Client &amp; case manuals</Typography>
						<Typography variant="body2" color="textSecondary">
							{caseType}
						</Typography>
					</Box>
					<IconButton size="small" aria-label="Close manuals" onClick={onClose}>
						<CloseIcon fontSize="small" />
					</IconButton>
				</Box>

				{manuals.length === 0 && (
					<Typography variant="body2" color="textSecondary">
						No manuals are written for this client and case type yet.
					</Typography>
				)}

				{manuals.length > 1 && (
					<Box display="flex" justifyContent="flex-end">
						<Button
							size="small"
							onClick={() => setOpen(allOpen ? new Set() : new Set(manuals.map((manual) => manual.Name)))}
						>
							{allOpen ? "Collapse all" : "Expand all"}
						</Button>
					</Box>
				)}

				<Box>
					{manuals.map((manual) => (
						<Accordion
							key={manual.Name}
							expanded={open.has(manual.Name)}
							onChange={() => toggle(manual.Name)}
							disableGutters
							square
							elevation={0}
							slotProps={{transition: {unmountOnExit: true}}}
							sx={{
								"&::before": {display: "none"},
								borderBottom: `1px solid ${colors.gray["200"]}`,
							}}
						>
							<AccordionSummary
								expandIcon={<ExpandMoreIcon />}
								sx={{
									px: 2,
									borderRadius: 1,
									bgcolor: colors.gray["50"],
									"&:hover": {bgcolor: colors.gray["100"]},
								}}
							>
								<Typography variant="subtitle2">{manual.Name}</Typography>
							</AccordionSummary>
							<AccordionDetails
								sx={{pl: 4, pr: 0, pt: 2, pb: 3, display: "flex", flexDirection: "column", gap: 1}}
							>
								<RuleText text={manual.Text} />
							</AccordionDetails>
						</Accordion>
					))}
				</Box>
			</Box>
		</Box>
	)
}

function firstExpanded(manuals: CaseManual[]): Set<string> {
	return new Set(manuals.slice(0, 1).map((manual) => manual.Name))
}

function clampWidth(width: number): number {
	return Math.min(Math.max(Math.round(width), minWidth), widestAllowed())
}

function widestAllowed(): number {
	return Math.max(minWidth, Math.floor(window.innerWidth * maxViewportShare))
}

function rememberedWidth(): number {
	const stored = Number(window.localStorage.getItem(widthKey))
	return stored > 0 ? stored : defaultWidth
}

function rememberWidth(width: number) {
	window.localStorage.setItem(widthKey, String(width))
}

export {CaseManualsPanel}
